import type { WarningNotice } from '@/pages/accounting/statements/statementMeta';
import { StatementWarnings } from '@/pages/accounting/statements/components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Label,
    SearchField,
    Switch,
    type Selection,
} from '@heroui/react';
import type { ChartAccount, LocaleCode } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ChartOfAccountsSkeleton } from './ChartOfAccountsSkeleton';
import { AccountFormDrawer, AccountTree } from './components';
import type { AccountDrawerState } from './useChartOfAccountsPage';
import type { BuildAccountTreeResult } from './utils';

type ChartOfAccountsPageViewProps = {
    /**
     * The chart is still in flight. The toolbar keeps its real labels and is
     * disabled; only the grid body is replaced by a skeleton.
     */
    isLoading: boolean;
    /**
     * Whether this session may add, edit or archive. A BranchAdmin reads the
     * whole chart and writes none of it — the write affordances are removed
     * (not disabled: there is no state in which they would become available)
     * and the reason is stated once, in the footer.
     */
    canWrite: boolean;
    /** Advisory for the abnormal balances on screen (BOS-163). */
    abnormalNotices: WarningNotice[];
    onDismissAbnormal: () => void;
    tree: BuildAccountTreeResult;
    /** The full chart — passed to the drawer for parent options + uniqueness. */
    accounts: ChartAccount[];
    locale: LocaleCode;
    search: string;
    showArchived: boolean;
    expandedKeys: Set<string>;
    onSearchChange: (value: string) => void;
    onToggleArchived: (next: boolean) => void;
    onExpandedChange: (keys: Selection) => void;
    onSelectAccount: (account: ChartAccount) => void;
    drawer: AccountDrawerState;
    onAddAccount: () => void;
    onEditAccount: (account: ChartAccount) => void;
    onReactivateAccount: (account: ChartAccount) => void;
    onCloseDrawer: () => void;
    onSavedAccount: () => void;
};

/**
 * Render-only Chart of Accounts screen: toolbar (search, show-archived toggle,
 * account count) + the account-tree grid + the read-only footer note.
 */
export const ChartOfAccountsPageView: React.FC<
    ChartOfAccountsPageViewProps
> = ({
    isLoading,
    canWrite,
    abnormalNotices,
    onDismissAbnormal,
    tree,
    accounts,
    locale,
    search,
    showArchived,
    expandedKeys,
    onSearchChange,
    onToggleArchived,
    onExpandedChange,
    onSelectAccount,
    drawer,
    onAddAccount,
    onEditAccount,
    onReactivateAccount,
    onCloseDrawer,
    onSavedAccount,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('chartOfAccounts.title')}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('chart-of-accounts-section')}
        >
            <div
                className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('chart-of-accounts-toolbar')}
            >
                <SearchField
                    aria-label={t('chartOfAccounts.searchAria')}
                    className="w-full sm:max-w-xs"
                    isDisabled={isLoading}
                    onChange={onSearchChange}
                    value={search}
                    {...getNodeTestIdProp('chart-of-accounts-search')}
                >
                    <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input
                            placeholder={t('chartOfAccounts.searchPlaceholder')}
                        />
                        <SearchField.ClearButton />
                    </SearchField.Group>
                </SearchField>

                {/* The switch and the count share a row; Add Account drops to
                    its own row beneath them, right-aligned, so a narrow screen
                    never squeezes the CTA off the edge. From `sm` up there is
                    room for all three on one line. */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
                    <div className="flex items-center justify-between gap-4">
                        <Switch
                            isDisabled={isLoading}
                            isSelected={showArchived}
                            onChange={onToggleArchived}
                            size="sm"
                            {...getNodeTestIdProp(
                                'chart-of-accounts-show-archived',
                            )}
                        >
                            {/* `switch__content` defaults to a column, which stacks
                            the control above the label. Force a centered row so
                            the toggle and "Show archived" sit on one line. */}
                            <Switch.Content className="flex-row items-center gap-2">
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Label className="whitespace-nowrap text-sm text-foreground">
                                    {t('chartOfAccounts.showArchived')}
                                </Label>
                            </Switch.Content>
                        </Switch>

                        <span
                            className="whitespace-nowrap text-sm text-muted tabular-nums"
                            {...getNodeTestIdProp('chart-of-accounts-count')}
                        >
                            {isLoading
                                ? t('chartOfAccounts.accountCountPending')
                                : t('chartOfAccounts.accountCount', {
                                      count: tree.visiblePostableCount,
                                  })}
                        </span>
                    </div>

                    {canWrite ? (
                        <div className="flex justify-end">
                            <Button
                                isDisabled={isLoading}
                                onPress={onAddAccount}
                                variant="primary"
                                {...getNodeTestIdProp('chart-of-accounts-add')}
                            >
                                {t('chartOfAccounts.actions.addAccount')}
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Why a balance is red, on the tab where the red is. The same
                advisory used to appear only on Statements, which a reader
                looking at the chart has no reason to visit (BOS-163). */}
            <StatementWarnings
                notices={abnormalNotices}
                onDismiss={onDismissAbnormal}
                testIdPrefix="chart-of-accounts"
            />

            <div
                className="overflow-hidden rounded-xl border border-border bg-surface"
                {...getNodeTestIdProp('chart-of-accounts-grid')}
            >
                {isLoading ? (
                    <ChartOfAccountsSkeleton />
                ) : (
                    <AccountTree
                        canWrite={canWrite}
                        expandedKeys={expandedKeys}
                        locale={locale}
                        onEditAccount={onEditAccount}
                        onExpandedChange={onExpandedChange}
                        onReactivateAccount={onReactivateAccount}
                        onSelectAccount={onSelectAccount}
                        roots={tree.roots}
                    />
                )}
            </div>

            <p
                className="text-xs text-muted"
                {...getNodeTestIdProp('chart-of-accounts-footer-note')}
            >
                {canWrite
                    ? t('chartOfAccounts.footerNote')
                    : t('chartOfAccounts.access.readOnly.message')}
            </p>

            {canWrite ? (
                <AccountFormDrawer
                    account={drawer.account}
                    accounts={accounts}
                    instanceId={drawer.instanceId}
                    isOpen={drawer.isOpen}
                    locale={locale}
                    onClose={onCloseDrawer}
                    onSaved={onSavedAccount}
                />
            ) : null}
        </section>
    );
};
